/**
 * ModulePlayer — dispatches a single learner module to the appropriate view
 * from `components/modules/views/registry`. Used by both the per-module
 * route (CourseModulePage) and any inline preview (e.g. CourseDetailPage).
 *
 * Evaluation and feedback modules are NOT rendered inline — they have their
 * own routes (`/evaluate`, `/feedback`). Pass them to `onLaunchEvaluation`
 * / `onLaunchFeedback` so the parent can navigate.
 */

import React from 'react';
import {
  RichTextView,
  ImageView,
  VideoView,
  PdfView,
  ScormView,
  LinkView,
  CodeFileView,
} from '../modules/views/registry';
import type { components } from '../../types/learnerApi';

type ContentModule = components['schemas']['ContentModule'];

interface ModulePlayerProps {
  module: ContentModule;
}

const ModulePlayer: React.FC<ModulePlayerProps> = ({ module }) => {
  const details = module.details ?? {};
  switch (module.type) {
    case 'rich_text':
      return (
        <RichTextView
          item={{
            content: (details as { content?: string }).content,
            title: module.title ?? undefined,
          }}
          mode="learner"
        />
      );
    case 'image_embed':
      return (
        <ImageView
          item={{
            image_url: (details as { image_url?: string }).image_url,
            alt_text: (details as { alt_text?: string }).alt_text,
            caption: (details as { caption?: string }).caption,
            title: module.title ?? undefined,
          }}
          mode="learner"
        />
      );
    case 'video_url_embed':
      return (
        <VideoView
          item={{
            video_url: (details as { video_url?: string }).video_url,
            transcript: (details as { transcript?: string }).transcript,
            title: module.title ?? undefined,
          }}
          mode="learner"
        />
      );
    case 'pdf_file':
      return (
        <PdfView
          item={{
            file_url: (details as { file_url?: string }).file_url,
            filename: (details as { filename?: string }).filename,
            size_bytes: (details as { size_bytes?: number }).size_bytes,
            title: module.title ?? undefined,
          }}
          mode="learner"
        />
      );
    case 'scorm_zip':
      return (
        <ScormView
          item={{
            file_url: (details as { file_url?: string }).file_url,
            title: module.title ?? undefined,
            module_id: module.id,
          }}
          mode="learner"
        />
      );
    case 'video_meeting_link':
      return (
        <LinkView
          item={{
            meeting_url: (details as { meeting_url?: string }).meeting_url,
            title: module.title,
          }}
          mode="learner"
        />
      );
    case 'source_code_file':
      return (
        <CodeFileView
          item={{
            code: (details as { code?: string }).code,
            language: (details as { language?: string }).language,
            filename: (details as { filename?: string }).filename,
            file_url: (details as { file_url?: string }).file_url,
            title: module.title ?? undefined,
          }}
          mode="learner"
        />
      );
    default:
      return (
        <p className="text-text-secondary text-sm italic">
          This content type isn't available in your learner runtime yet.
        </p>
      );
  }
};

export default ModulePlayer;
